import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MyContext } from '../../../context';
import DataTable from '../../../components/tables/DataTable';
import Pagination from '../../../components/Pagination';
import { listWorkflowExceptions, retryWorkflowException, skipWorkflowException, cancelWorkflowException } from '../../../utils/queries';

const PAGE_SIZE = 20;

export default function WorkflowExceptionsList() {
	const { activeStoreId } = useContext(MyContext);
	const [exceptions, setExceptions] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [working, setWorking] = useState(null);

	const refresh = () => {
		if (!activeStoreId) return;
		setLoading(true);
		listWorkflowExceptions({ storeId: activeStoreId, status: 'OPEN', limit: PAGE_SIZE, offset: page * PAGE_SIZE }).then((result) => {
			if (result?.success) {
				setExceptions(result.data.exceptions);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, activeStoreId]);

	const runAction = async (fn, row, successMessage) => {
		setWorking(row.workflow_exception_id);
		const result = await fn(row.workflow_exception_id, activeStoreId);
		setWorking(null);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Action failed.');
			return;
		}
		toast.success(successMessage);
		refresh();
	};

	const columns = [
		{
			key: 'workflow_name',
			header: 'Workflow',
			render: (value, row) => (
				<Link to={`/workflows/runs/${row.workflow_instance_id}`} className='text-black font-medium hover:underline'>
					{value || `Workflow #${row.workflow_instance_id}`}
				</Link>
			),
		},
		{ key: 'entity_id', header: 'Entity', render: (value, row) => `${row.entity_type} #${value}` },
		{ key: 'step_id', header: 'Step' },
		{ key: 'error_message', header: 'Error', render: (value) => value || '—' },
		{ key: 'opened_at', header: 'Opened', render: (value) => (value ? new Date(value).toLocaleString() : '-') },
		{
			key: 'workflow_exception_id',
			header: '',
			sortable: false,
			render: (_value, row) => (
				<div className='flex gap-2 justify-end'>
					<button type='button' className='btn-secondary-small' disabled={working === row.workflow_exception_id} onClick={() => runAction(cancelWorkflowException, row, 'Cancelled.')}>
						Cancel
					</button>
					<button type='button' className='btn-secondary-small' disabled={working === row.workflow_exception_id} onClick={() => runAction(skipWorkflowException, row, 'Skipped.')}>
						Skip
					</button>
					<button type='button' className='btn-primary-small' disabled={working === row.workflow_exception_id} onClick={() => runAction(retryWorkflowException, row, 'Retrying...')}>
						Retry
					</button>
				</div>
			),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>Workflow Exceptions</h1>
			<DataTable columns={columns} data={exceptions} loading={loading} emptyHeading='Nothing needs attention' emptyMessage='Failed workflow steps show up here.' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
